// Reads a video container — the append-only list of published versions — and
// every VersionRef under it. Wallet-free by construction: a stranger has to be
// able to check that the version they are watching is the current one, and a
// public viewer has no wallet and must never be asked for one. Like
// useForumVideo, this needs only a connection (via useProgramReader) and works
// under a bare ConnectionProvider.
import { useConnection } from '@solana/wallet-adapter-react'
import type { AccountInfo, PublicKey } from '@solana/web3.js'
import { PublicKey as PublicKeyImpl } from '@solana/web3.js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useProgramReader } from '../react/index.js'
import { videoIdBytes } from './encoding.js'
import {
  publisherPda as derivePublisherPda,
  containerPda as deriveContainerPda,
  versionRefPda as deriveVersionRefPda,
} from './pda.js'
import { FORUM_IDL, FORUM_PROGRAM_ID, type VideoContainerAccount, type VersionRefAccount } from './program.js'

/** A VersionRef paired with the address it was read from, so a caller can
 * show or link the account it is trusting without re-deriving the seed. */
export interface ForumVersionRef {
  pda: PublicKey
  ref: VersionRefAccount
}

export interface ForumContainerState {
  publisherPda: PublicKey | null
  containerPda: PublicKey | null
  container: VideoContainerAccount | null
  /** Decoded refs, ascending by index. Never longer than the container's
   * versionCount; a caller comparing the two is comparing what the chain
   * claims against what it could actually produce. */
  versions: ForumVersionRef[]
  loading: boolean
  /** True when the container account genuinely does not exist — this video was
   * never anchored as a container — as opposed to not-loaded-yet. The panel
   * says "unanchored" for the first and stays quiet for the second, so these
   * must not collapse into one null. */
  missing: boolean
  error: Error | null
  refresh: () => void
}

/** getMultipleAccounts rejects more than 100 addresses in one request. A u16
 * version count will not reach that for a very long time, but the loop must
 * not assume a single call suffices. */
const MAX_ACCOUNTS_PER_FETCH = 100

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

interface ContainerReadState {
  container: VideoContainerAccount | null
  loading: boolean
  missing: boolean
  error: Error | null
}

interface VersionsReadState {
  versions: ForumVersionRef[]
  loading: boolean
  error: Error | null
}

/**
 * Reads need no wallet, but a container PDA is derived from the *publisher's*
 * PDA, which is derived from an authority key. With no authority there is
 * nothing to derive from: this returns a null containerPda and missing: false,
 * never a fabricated address and never "this was never anchored".
 */
export function useForumContainer(containerId: string, authority: string | null): ForumContainerState {
  const { connection } = useConnection()
  const reader = useProgramReader(FORUM_IDL, FORUM_PROGRAM_ID)
  const [generation, setGeneration] = useState(0)
  const refresh = useCallback(() => setGeneration((g) => g + 1), [])

  // Both derivations throw on malformed input — an authority that is not a
  // base58 key, or a container id that is not a UUID. Throwing during render
  // would white-screen a whole player page over a chain widget, so they
  // degrade to null and the caller shows "not anchored" instead.
  const publisherPda = useMemo(() => {
    if (!authority) return null
    try {
      return derivePublisherPda(new PublicKeyImpl(authority))
    } catch {
      return null
    }
  }, [authority])

  const containerPda = useMemo(() => {
    if (!publisherPda) return null
    try {
      return deriveContainerPda(publisherPda, videoIdBytes(containerId))
    } catch {
      return null
    }
  }, [publisherPda, containerId])

  const [containerState, setContainerState] = useState<ContainerReadState>({
    container: null,
    loading: containerPda !== null,
    missing: false,
    error: null,
  })

  useEffect(() => {
    if (!containerPda) {
      setContainerState({ container: null, loading: false, missing: false, error: null })
      return
    }

    let live = true
    setContainerState((s) => ({ ...s, loading: true }))

    // Decoded through the Program's own coder, which converts the IDL's Rust
    // field names to the camelCase the generated types promise. A bare
    // BorshAccountsCoder built from the raw JSON does not, and every field
    // reads undefined while TypeScript stays happy.
    //
    // Named 'videoContainer' rather than decodeAny: decodeAny would happily
    // decode whatever account sits at this address and hand it back cast as a
    // VideoContainerAccount. Naming it checks the discriminator and throws.
    const apply = (raw: AccountInfo<Buffer> | null) => {
      if (!live) return
      if (!raw) {
        setContainerState({ container: null, loading: false, missing: true, error: null })
        return
      }
      try {
        const decoded = reader.coder.accounts.decode<VideoContainerAccount>('videoContainer', raw.data)
        setContainerState({ container: decoded, loading: false, missing: false, error: null })
      } catch (err) {
        setContainerState({ container: null, loading: false, missing: false, error: err as Error })
      }
    }

    connection
      .getAccountInfo(containerPda)
      .then(apply)
      .catch(
        (err: Error) => live && setContainerState({ container: null, loading: false, missing: false, error: err }),
      )

    // Subscribed, not polled: appending a version increments versionCount on
    // this one account, which re-runs the refs read below without the page
    // asking. A viewer watching while a publisher ships v2 is told.
    const sub = connection.onAccountChange(containerPda, apply)
    return () => {
      live = false
      // Leaking subscriptions keeps the websocket busy and the component alive.
      void connection.removeAccountChangeListener(sub)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reader is a fresh
    // object every render; keyed on the address string and an explicit
    // refresh trigger instead, same discipline as useForumVideo.
  }, [connection, containerPda?.toBase58(), generation])

  const versionCount = containerState.container?.versionCount ?? 0

  const [versionsState, setVersionsState] = useState<VersionsReadState>({
    versions: [],
    loading: false,
    error: null,
  })

  useEffect(() => {
    // A container that exists with no versions yet is an ordinary, legible
    // state — an empty list, not an error and not a failed read.
    if (!containerPda || versionCount === 0) {
      setVersionsState({ versions: [], loading: false, error: null })
      return
    }

    let live = true
    setVersionsState((s) => ({ ...s, loading: true }))

    const pdas: PublicKey[] = []
    for (let index = 0; index < versionCount; index++) {
      pdas.push(deriveVersionRefPda(containerPda, index))
    }

    const load = async () => {
      const infos: (AccountInfo<Buffer> | null)[] = []
      for (const batch of chunk(pdas, MAX_ACCOUNTS_PER_FETCH)) {
        infos.push(...(await connection.getMultipleAccountsInfo(batch)))
      }

      const versions: ForumVersionRef[] = []
      infos.forEach((raw, index) => {
        if (!raw) return
        try {
          versions.push({
            pda: pdas[index],
            ref: reader.coder.accounts.decode<VersionRefAccount>('versionRef', raw.data),
          })
        } catch {
          // One unreadable ref is a hole in the list, not a failed read of the
          // container. Dropping it keeps versions.length short of
          // versionCount, which is exactly the disagreement a caller should
          // see and report — swallowing it into an error would hide the
          // refs that did decode.
        }
      })
      return versions
    }

    load()
      .then((versions) => live && setVersionsState({ versions, loading: false, error: null }))
      .catch((err: Error) => live && setVersionsState({ versions: [], loading: false, error: err }))

    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above; the
    // container's address and its version count are what this read depends on.
  }, [connection, containerPda?.toBase58(), versionCount, generation])

  return {
    publisherPda,
    containerPda,
    container: containerState.container,
    versions: versionsState.versions,
    loading: containerState.loading || versionsState.loading,
    missing: containerState.missing,
    error: containerState.error ?? versionsState.error,
    refresh,
  }
}
